import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgModel } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { AutofocusDirective, LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import {
  AuthEmailChange,
  AuthPayloadEnrollDevice,
  AuthPayloadMoneyTx,
  AuthPayloadRetireDevice,
  AuthPayloadSessionAuth,
  AuthPayloadTicket,
  AuthorizationData,
  EnrolledDevice,
  IBackendResponse,
  MoneyTransaction,
  MoneyTransactionSubmittedData,
  Ticket,
  TransactionAuthorizationResult,
} from '@zero/shared/util-models';
import { TicketService } from '@zero/shared/util-services';
import {
  AuthService,
  DataService,
  HttpResponseHandleHelper,
  MoneyTransactionService,
  SessionTanService,
} from '@zero/shared/util-web-app-services';
import { POLLING_FOR_AUTHORIZATION_INTERVAL_MS } from 'constants/shared-constants';
import { environment } from 'environments/environment';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { Observable, Subscription, interval, of } from 'rxjs';
import { filter, first, switchMap, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'web-components-transaction-auth',
  templateUrl: './transaction-authorization.component.html',
  imports: [LoadingIndicatorDirective, FormsModule, AutofocusDirective, DatePipe, ButtonComponent, TranslocoPipe],
})
export class TransactionAuthorizationComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public payload: AuthorizationData<any>;

  @Output() public transactionResult = new EventEmitter<TransactionAuthorizationResult>();

  readonly #dataService = inject(DataService);
  readonly #http = inject(HttpClient);
  readonly #authService = inject(AuthService);
  readonly #mtService = inject(MoneyTransactionService);
  readonly #tanService = inject(SessionTanService);
  readonly #ticketService = inject(TicketService);
  readonly #destroyRef = inject(DestroyRef);

  #hasAppEnrolled = false;
  #timerHandleOfferTanResend: any;
  #pollingForAuthSubscriptionsForAuthIds: Record<
    string,
    {
      sub: Subscription;
      authState: string;
      ongoingPollRequest?: boolean;
    }
  > = {};

  public enrolledTANDevices: EnrolledDevice[];
  public chosenTANDeviceName: string;
  public tanMode = false;
  public appMode = false;
  public tanSuccessfullySent: boolean;
  public errors: string[] = [];
  public transactionCreated: boolean;
  public lastTanRequested: string;
  public verifiedPhoneNumber: string;
  public waitingForTransactionAuth: boolean;
  public transactionAbortedThroughApp: boolean;
  public isTradingSessionAuth: boolean;
  public ongoingRequest = false;
  public ttsEnabled = false;
  public lastTanWasTts = false;
  public offerTanResend = false;
  public appAuthEnabled = false;
  public readonly speechNr = environment.speechNr;

  constructor(@Inject(LOCAL_STORAGE) private storage: StorageService) {}

  ngOnInit(): void {
    this.#dataService
      .isAppAuthenticationEnabled()
      .pipe(
        switchMap((appAuthEnabled) => {
          if (appAuthEnabled) {
            this.appAuthEnabled = true;
            return this.#dataService.getEnrolledDevices$().pipe(
              first((val) => !!val), // take only first non-null value
            );
          } else {
            return of([]);
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((devices) => {
        let filteredDevices = devices;
        if (this.#getPayloadType() === AuthPayloadRetireDevice.typename) {
          // when you authorize a retirement, we have to filter out the device that should be retired
          filteredDevices = devices.filter((dev) => dev.serial !== (this.payload.data as AuthPayloadRetireDevice).serial);
        }

        this.#hasAppEnrolled = filteredDevices.length > 0;
        this.enrolledTANDevices = filteredDevices;

        if (this.#hasAppEnrolled) {
          if (!this.chosenTANDeviceName) {
            this.chosenTANDeviceName = this.#getLastUsedOrFirstAuthDeviceName(this.enrolledTANDevices);
          }
          this.appMode = true;
          this.initiateAppAuth();
        } else {
          this.tanMode = true;
        }
      });

    this.verifiedPhoneNumber = this.#authService.getCurrentVerifiedPhoneNumber();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payload']?.currentValue) {
      this.isTradingSessionAuth = this.#getPayloadType() === AuthPayloadSessionAuth.typename;

      // speech is allowed, we check if speech tan is available
      this.#tanService
        .isTtsEnabled()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((resultTts) => {
          if (resultTts.success) {
            this.ttsEnabled = resultTts.data;
          } else {
            this.errors.push(resultTts.message);
            this.ttsEnabled = false;
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.#abortAllAuthsAndStopPolling();
    this.#stopOfferTanResendTimer();
  }

  #abortAllAuthsAndStopPolling(): void {
    const authIds = Object.keys(this.#pollingForAuthSubscriptionsForAuthIds);
    for (const authId of authIds) {
      this.#abortAuthAndStopPolling(authId);
    }
  }

  #abortAuthAndStopPolling(authId: string): void {
    const authData = this.#pollingForAuthSubscriptionsForAuthIds[authId];
    if (authData.sub) {
      authData.sub.unsubscribe();
    }
    this.#abortAuth(authId);
    delete this.#pollingForAuthSubscriptionsForAuthIds[authId];
  }

  public initiateAppAuth(): void {
    this.errors = [];

    this.#abortAllAuthsAndStopPolling();

    this.#saveLastUsedDeviceName(this.chosenTANDeviceName);

    const payloadType = this.#getPayloadType();
    switch (payloadType) {
      case AuthPayloadEnrollDevice.typename:
        this.#requestAppAuthEnrollDevice();
        break;
      case AuthPayloadMoneyTx.typename:
        this.#requestAppAuthMoneyTx();
        break;
      case AuthPayloadSessionAuth.typename:
        this.#requestAppAuthTradingSession();
        break;
      case AuthPayloadRetireDevice.typename:
        this.requestAppAuthRetireDevice();
        break;
      case AuthPayloadTicket.typename:
        this.#requestAppAuthTicket();
        break;
      case AuthEmailChange.typename:
        this.#requestAppAuthChangeEmail();
        break;
      default:
        this.errors.push('Fehler beim Starten der Autorisierung.');
    }
  }

  public initiateTanAuth(tanViaTTS?: boolean, currentTanVal?: NgModel): void {
    this.errors = [];
    this.lastTanWasTts = !!tanViaTTS;
    this.offerTanResend = false;
    this.#startOfferTanResendTimer();
    if (currentTanVal?.value) {
      currentTanVal.reset();
    }

    const payloadType = this.#getPayloadType();
    switch (payloadType) {
      case AuthPayloadEnrollDevice.typename:
        this.#requestTanEnrollDevice(tanViaTTS);
        break;
      case AuthPayloadMoneyTx.typename:
        this.#requestTanMoneyTx();
        break;
      case AuthPayloadSessionAuth.typename:
        this.#requestTanTradingSession(tanViaTTS);
        break;
      case AuthPayloadRetireDevice.typename:
        this.#requestTanRetireDevice(tanViaTTS);
        break;
      case AuthPayloadTicket.typename:
        this.#requestTanTicket(tanViaTTS);
        break;
      case AuthEmailChange.typename:
        this.#requestTanChangeEmail(tanViaTTS);
        break;
      default:
        this.errors.push('Fehler beim Anfordern einer TAN');
    }
  }

  #getPayloadType(): string {
    if (!this.payload?.data) {
      return undefined;
    }

    // constructor.name is useless when code is minified
    return this.payload.data.getTypename ? this.payload.data.getTypename() : this.payload.data.constructor.name;
  }

  #requestTanEnrollDevice(tanViaTTS?: boolean): void {
    const deviceName = (this.payload.data as AuthPayloadEnrollDevice).deviceName;
    tanViaTTS ??= false;

    HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.appEnrollmentUrl}/requestenrollmenttan`, deviceName, {
        params: HttpResponseHandleHelper.skipGlobalHandler().append('useSpeech', String(tanViaTTS)),
      }),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((reponse) => {
        if (reponse.success) {
          this.tanSuccessfullySent = reponse.data;
          this.lastTanRequested = new Date().toISOString();
        } else {
          this.tanSuccessfullySent = false;
          this.errors.push(reponse.message);
        }
      });
  }

  public createTransaction(tanToSubmit?: string, authorizationIdToSubmit?: string): void {
    this.errors = [];
    const payloadType = this.#getPayloadType();
    switch (payloadType) {
      case AuthPayloadEnrollDevice.typename:
        this.#initiateAppEnrollment(tanToSubmit, authorizationIdToSubmit);
        break;
      case AuthPayloadMoneyTx.typename:
        this.#createMoneyTx(tanToSubmit, authorizationIdToSubmit);
        break;
      case AuthPayloadSessionAuth.typename:
        this.#createTradingSession(tanToSubmit, authorizationIdToSubmit);
        break;
      case AuthPayloadRetireDevice.typename:
        this.#createAppRetirement(tanToSubmit, authorizationIdToSubmit);
        break;
      case AuthPayloadTicket.typename:
        this.#verifyAndOpenTicket(tanToSubmit, authorizationIdToSubmit);
        break;
      case AuthEmailChange.typename:
        this.#verifyEmailChange(tanToSubmit, authorizationIdToSubmit);
        break;
      default:
        this.errors.push('Unbekannte Transaktionsart');
    }
  }

  #initiateAppEnrollment(tanToSubmit?: string, authorizationIdToSubmit?: string): void {
    const deviceName = (this.payload.data as AuthPayloadEnrollDevice).deviceName;

    const requestBody = {
      tan: tanToSubmit,
      customerDeviceName: deviceName,
      authorizationId: authorizationIdToSubmit,
    };
    HttpResponseHandleHelper.handleResponse(
      this.#http.post<{
        initiateCode: string;
        reasonForInitiateCodeNull?: string;
        qrCodeBase64?: string;
      }>(`${environment.apihost + environment.appEnrollmentUrl}/enrolldevice`, requestBody, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.success) {
          this.transactionCreated = true;
          if (authorizationIdToSubmit) {
            delete this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToSubmit];
          }
          this.transactionResult.emit({
            success: true,
            data: response.data.initiateCode,
            imgData: response.data.qrCodeBase64,
          });
        } else {
          this.transactionCreated = false;
          this.errors.push(response.message);
          this.transactionResult.emit({
            success: false,
            reason: response.message,
          });
        }
      });
  }

  public cancelAuth(): void {
    this.#abortAllAuthsAndStopPolling();

    this.transactionResult.emit({ success: false, reason: 'CanceledByUser' });
  }

  public switchToTanMode(): void {
    this.#abortAllAuthsAndStopPolling();

    this.tanMode = true;
    this.appMode = false;
  }

  #requestAppAuthEnrollDevice(): void {
    this.errors = [];

    const chosenDevice = this.#getChosenDevice();
    const deviceName = (this.payload.data as AuthPayloadEnrollDevice).deviceName;

    this.ongoingRequest = true;
    HttpResponseHandleHelper.handleResponse(
      this.#http.post(
        `${environment.apihost + environment.appEnrollmentUrl}/requestenrollmentauth`,
        {
          serialForAppAuth: chosenDevice.serial,
          deviceNameToEnroll: deviceName,
        },
        {
          responseType: 'text',
          params: HttpResponseHandleHelper.skipGlobalHandler(),
        },
      ),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.success) {
          this.#triggerPollingForAuth(response.data);
        } else {
          this.errors.push(response.message);
        }
        this.ongoingRequest = false;
      });
  }

  #getChosenDevice(): EnrolledDevice {
    return this.enrolledTANDevices.find((dev) => dev.customerDeviceName === this.chosenTANDeviceName);
  }

  #triggerPollingForAuth(authorizationIdToPoll: string): void {
    this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll] = {
      sub: undefined,
      authState: undefined,
    };

    this.waitingForTransactionAuth = true;
    this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll].sub = interval(POLLING_FOR_AUTHORIZATION_INTERVAL_MS)
      .pipe(
        takeWhile(() => !this.#isAuthStateFinal(authorizationIdToPoll)),
        filter(() => !this.#isOngoingPollrequest(authorizationIdToPoll)),
        switchMap(() => {
          this.errors = [];
          this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll].ongoingPollRequest = true;
          return HttpResponseHandleHelper.handleResponse(
            this.#http.get(`${environment.apihost + environment.appAuthUrl}/authstate/${authorizationIdToPoll}`, {
              responseType: 'text',
              params: HttpResponseHandleHelper.skipGlobalHandler(),
            }),
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((response) => {
        if (response.success) {
          this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll].authState = response.data;
          if (this.#isAuthStateFinal(authorizationIdToPoll)) {
            this.waitingForTransactionAuth = false;
            if (this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll].authState === 'AUTHORIZATION_USER_ACCEPTED') {
              this.createTransaction(null, authorizationIdToPoll);
            } else {
              this.transactionAbortedThroughApp = true;
            }
          }
        } else {
          this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll].authState = 'SERVERSIDE_CANCEL';
          this.errors.push(response.message);
        }
        this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToPoll].ongoingPollRequest = false;
      });
  }

  #isAuthStateFinal(authorizationId: string): boolean {
    return (
      !this.#pollingForAuthSubscriptionsForAuthIds[authorizationId] ||
      ['AUTHORIZATION_ABORTED', 'AUTHORIZATION_USER_ACCEPTED', 'AUTHORIZATION_USER_CANCELED', 'SERVERSIDE_CANCEL'].indexOf(
        this.#pollingForAuthSubscriptionsForAuthIds[authorizationId].authState,
      ) > -1
    );
  }

  #abortAuth(authorizationId: string): void {
    this.ongoingRequest = true;
    const authorizationIdToAbort = authorizationId;
    HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.appAuthUrl}/abortauth`, authorizationIdToAbort, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.success) {
          if (response.data) {
            if (this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToAbort]) {
              this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToAbort].authState = undefined;
            }
          }
        } else {
          this.errors.push(response.message);
        }
        this.ongoingRequest = false;
      });
  }

  #requestTanMoneyTx(): void {
    const data2send: MoneyTransaction = (this.payload.data as AuthPayloadMoneyTx).data;

    this.ongoingRequest = true;
    this.#mtService
      .requestTAN(data2send)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.success) {
          if (result.data === true) {
            this.tanSuccessfullySent = true;
            this.lastTanRequested = new Date().toISOString();
          }
        } else {
          this.tanSuccessfullySent = false;
          this.errors.push(result.message);
        }
        this.ongoingRequest = false;
      });
  }

  #createMoneyTx(tanToSubmit?: string, authorizationIdToSubmit?: string): void {
    this.errors = [];
    const data2send: MoneyTransactionSubmittedData = new MoneyTransactionSubmittedData();
    data2send.moneyTransactionData = (this.payload.data as AuthPayloadMoneyTx).data;
    data2send.tan = tanToSubmit;
    data2send.authorizationId = authorizationIdToSubmit;

    this.ongoingRequest = true;
    this.#mtService
      .createMoneyTransaction(data2send)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.success) {
          if (result.data === true) {
            this.transactionCreated = true;
            if (authorizationIdToSubmit) {
              delete this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToSubmit];
            }
            this.transactionResult.emit({ success: true });
          } else {
            this.transactionCreated = false;
            this.errors.push(result.message ?? 'Deine eingegebene TAN ist nicht korrekt.');
          }
        }
        if (result.message) {
          this.errors.push(result.message);
        }

        this.ongoingRequest = false;
      });
  }

  #requestAppAuthMoneyTx(): void {
    this.errors = [];

    const chosenDevice = this.#getChosenDevice();
    const moneyTx = (this.payload.data as AuthPayloadMoneyTx).data;

    this.ongoingRequest = true;
    this.#mtService
      .requestAppAuth(chosenDevice.serial, moneyTx)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.success) {
          this.#triggerPollingForAuth(result.data);
        } else {
          this.errors.push(result.message);
        }
        this.ongoingRequest = false;
      });
  }

  #requestAppAuthTradingSession(): void {
    this.errors = [];

    const chosenDevice = this.#getChosenDevice();
    this.ongoingRequest = true;
    this.#tanService
      .requestAppAuth(chosenDevice.serial)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.success) {
          this.#triggerPollingForAuth(result.data);
        } else {
          this.errors.push(result.message);
        }
        this.ongoingRequest = false;
      });
  }

  #requestTanTradingSession(tanViaTTS?: boolean): void {
    this.ongoingRequest = true;
    this.#tanService
      .requestSessionTAN(!!tanViaTTS)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.success) {
          this.tanSuccessfullySent = true;
          this.lastTanRequested = new Date().toISOString();
        } else {
          this.tanSuccessfullySent = false;
          this.errors.push('Fehler beim Versand der TAN');
        }
        if (result.message) {
          this.errors.push(result.message);
        }
        this.ongoingRequest = false;
      });
  }

  #createTradingSession(tanToSubmit?: string, authorizationIdToSubmit?: string): void {
    let verifyFn: Observable<IBackendResponse<boolean>>;
    let defaultVerificationFaildMsg: string;
    if (tanToSubmit) {
      verifyFn = this.#tanService.verifySessionTAN(tanToSubmit);
      defaultVerificationFaildMsg = 'Deine eingegebene TAN ist nicht korrekt.';
    } else {
      verifyFn = this.#tanService.verifyAppAuth(authorizationIdToSubmit);
      defaultVerificationFaildMsg = 'Fehler bei der Freigabe der Handelssession.';
    }

    this.ongoingRequest = true;
    verifyFn.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result.success) {
        if (result.data) {
          this.transactionCreated = true;
          if (authorizationIdToSubmit) {
            delete this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToSubmit];
          }
          this.transactionResult.emit({ success: true });
        } else {
          this.transactionCreated = false;
        }
        if (result.data !== true && result.message) {
          this.errors.push(result.message ?? defaultVerificationFaildMsg);
        }
      } else if (result.message) {
        this.errors.push(result.message);
      }
      this.ongoingRequest = false;
    });
  }

  public requestAppAuthRetireDevice(): void {
    this.errors = [];

    const chosenDevice = this.#getChosenDevice();
    const deviceNameToRetire = (this.payload.data as AuthPayloadRetireDevice).customerDeviceName;
    const serialToRetire = (this.payload.data as AuthPayloadRetireDevice).serial;

    this.ongoingRequest = true;
    HttpResponseHandleHelper.handleResponse(
      this.#http.post(
        `${environment.apihost + environment.appEnrollmentUrl}/requestretirementauth`,
        {
          serialForAppAuth: chosenDevice.serial,
          customerDeviceName: deviceNameToRetire,
          serial: serialToRetire,
        },
        {
          responseType: 'text',
          params: HttpResponseHandleHelper.skipGlobalHandler(),
        },
      ),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.success) {
          if (response.data) {
            this.#triggerPollingForAuth(response.data);
          }
        } else {
          this.errors.push(response.message);
        }
        this.ongoingRequest = false;
      });
  }

  #requestTanRetireDevice(tanViaTTS?: boolean): void {
    const deviceNameToRetire = (this.payload.data as AuthPayloadRetireDevice).customerDeviceName;
    const serialToRetire = (this.payload.data as AuthPayloadRetireDevice).serial;
    tanViaTTS ??= false;

    this.ongoingRequest = true;
    HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(
        `${environment.apihost + environment.appEnrollmentUrl}/requestretirementtan`,
        {
          customerDeviceName: deviceNameToRetire,
          serial: serialToRetire,
        },
        {
          params: HttpResponseHandleHelper.skipGlobalHandler().append('useSpeech', String(tanViaTTS)),
        },
      ),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.success) {
          this.tanSuccessfullySent = true;
          this.lastTanRequested = new Date().toISOString();
        } else {
          this.tanSuccessfullySent = false;
          this.errors.push(response.message);
        }
        this.ongoingRequest = false;
      });
  }

  #createAppRetirement(tanToSubmit: string, authorizationIdToSubmit: string): void {
    const deviceNameToRetire = (this.payload.data as AuthPayloadRetireDevice).customerDeviceName;
    const serialToRetire = (this.payload.data as AuthPayloadRetireDevice).serial;

    const requestBody = {
      tan: tanToSubmit,
      authorizationId: authorizationIdToSubmit,
      customerDeviceName: deviceNameToRetire,
      serial: serialToRetire,
    };
    this.ongoingRequest = true;
    HttpResponseHandleHelper.handleResponse(
      this.#http.post<boolean>(`${environment.apihost + environment.appEnrollmentUrl}/retiredevice`, requestBody, {
        params: HttpResponseHandleHelper.skipGlobalHandler(),
      }),
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.success) {
          if (response.data) {
            this.transactionCreated = true;
            if (authorizationIdToSubmit) {
              delete this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToSubmit];
            }
            this.#dataService.triggerReloadEnrolledDevices();
            this.transactionResult.emit({ success: true });
          } else {
            this.transactionCreated = false;
            let errorMessageToShow = 'Fehler bei der Deregistrierung des Geräts.';
            if (requestBody.tan) {
              errorMessageToShow = 'Deine eingegebene TAN ist nicht korrekt.';
            } else {
              this.transactionResult.emit({
                success: false,
                reason: errorMessageToShow,
              });
            }
            this.errors.push(errorMessageToShow);
          }
        } else {
          this.transactionCreated = false;
          this.errors.push(response.message);
          this.transactionResult.emit({
            success: false,
            reason: response.message,
          });
        }
        this.ongoingRequest = false;
      });
  }

  #getLastUsedOrFirstAuthDeviceName(enrolledTANDevices: EnrolledDevice[]): string {
    const user = this.#authService.getCurrentUserName();
    const lsKey = `lastUsedDevice_${user}`;
    const lsSavedName = this.storage.get(lsKey);
    let foundDeviceName: string;
    foundDeviceName = enrolledTANDevices[0].customerDeviceName;
    if (lsSavedName) {
      const foundDevice = enrolledTANDevices.find((dev) => dev.customerDeviceName === lsSavedName);
      if (foundDevice?.customerDeviceName) {
        foundDeviceName = foundDevice.customerDeviceName;
      }
    }
    return foundDeviceName;
  }

  #saveLastUsedDeviceName(customerDeviceName: string): void {
    if (!customerDeviceName) {
      return;
    }

    const user = this.#authService.getCurrentUserName();
    const lsKey = `lastUsedDevice_${user}`;
    if (user) {
      this.storage.set(lsKey, customerDeviceName);
    }
  }

  #requestTanTicket(tanViaTTS?: boolean): void {
    this.errors = [];

    const data2send: Ticket = (this.payload.data as AuthPayloadTicket).data;
    this.ongoingRequest = true;
    this.#ticketService
      .requestTAN(data2send, !!tanViaTTS)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((response) => {
        if (response.data) {
          const ticket = response.data;
          data2send.id = ticket.id;
          if (ticket.status === 'PREPARED') {
            this.tanSuccessfullySent = true;
            this.lastTanRequested = new Date().toISOString();
          } else if (ticket.status === 'OPEN') {
            this.tanSuccessfullySent = false;
            this.errors.push('Du hast bereits den Auftrag bestätigt, er ist bereits in Bearbeitung...');
          }
        } else {
          this.tanSuccessfullySent = false;
          this.errors.push(response.errorMessage);
        }
        this.ongoingRequest = false;
      });
  }

  #requestAppAuthTicket(): void {
    this.errors = [];

    const chosenDevice = this.#getChosenDevice();
    const ticket = (this.payload.data as AuthPayloadTicket).data;

    this.ongoingRequest = true;
    this.#ticketService
      .requestAppAuth(chosenDevice.serial, ticket)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.data) {
          ticket.id = result.data.ticketId;
          this.#triggerPollingForAuth(result.data.authorizationId);
        } else {
          this.errors.push(result.errorMessage);
          this.transactionResult.emit({ success: false, reason: result.errorMessage });
        }
        this.ongoingRequest = false;
      });
  }

  #requestAppAuthChangeEmail(): void {
    this.errors = [];

    const chosenDevice = this.#getChosenDevice();
    const data = this.payload.data as AuthEmailChange;

    this.ongoingRequest = true;
    this.#http
      .post<any>(`${environment.apihost}/api/emailchange/requestauth`, {
        serialForAppAuth: chosenDevice.serial,
        emailChange: data,
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (response) => {
          if (response) {
            this.#triggerPollingForAuth(response.authorizationId);
          } else {
            this.errors.push(response.error);
          }
          this.ongoingRequest = false;
        },
        error: (response) => {
          this.errors.push(response.error.message);
          this.ongoingRequest = false;
        },
      });
  }

  #requestTanChangeEmail(tanViaTTS?: boolean): void {
    this.errors = [];

    const data = this.payload.data as AuthEmailChange;
    this.ongoingRequest = true;
    this.#http
      .post<boolean>(`${environment.apihost}/api/emailchange/requesttan`, data.emailChangeData.additionalProperties.requestId, {
        headers: {
          'Content-Type': 'application/json',
        },
        params: HttpResponseHandleHelper.skipGlobalHandler().append('useSpeech', String(!!tanViaTTS)),
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.tanSuccessfullySent = true;
          this.lastTanRequested = new Date().toISOString();
          this.ongoingRequest = false;
        },
        error: (response) => {
          this.tanSuccessfullySent = false;
          this.errors.push(response.error.message);
          this.ongoingRequest = false;
        },
      });
  }

  #verifyEmailChange(tanToSubmit: string, authorizationIdToSubmit: string): void {
    this.errors = [];
    const data = this.payload.data as AuthEmailChange;

    this.ongoingRequest = true;
    this.#http
      .post<boolean>(`${environment.apihost}/api/emailchange/verify`, {
        tan: tanToSubmit,
        authorizationId: authorizationIdToSubmit,
        requestId: data.emailChangeData.additionalProperties.requestId,
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.transactionResult.emit({ success: true });
          this.ongoingRequest = false;
        },
        error: () => {
          this.errors.push('Fehler bei der Bestätigung der E-Mail-Änderung');
          this.transactionResult.emit({ success: false, reason: 'Fehler bei der Bestätigung der E-Mail-Änderung' });
          this.ongoingRequest = false;
        },
      });
  }

  #verifyAndOpenTicket(tanToSubmit: string, authorizationIdToSubmit: string): void {
    this.errors = [];
    const ticket = (this.payload.data as AuthPayloadTicket).data;

    this.ongoingRequest = true;
    this.#ticketService
      .verifyTicket(ticket, tanToSubmit, authorizationIdToSubmit)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result) => {
        if (result.data) {
          this.transactionResult.emit({ success: true, data: result.data });
          this.transactionCreated = true;
          if (authorizationIdToSubmit) {
            delete this.#pollingForAuthSubscriptionsForAuthIds[authorizationIdToSubmit];
          }
        } else {
          this.transactionCreated = false;
          this.errors.push(result.errorMessage);
          this.transactionResult.emit({ success: false, reason: result.errorMessage });
        }
        this.ongoingRequest = false;
      });
  }

  #stopOfferTanResendTimer(): void {
    this.#timerHandleOfferTanResend && clearTimeout(this.#timerHandleOfferTanResend);
  }

  #startOfferTanResendTimer(): void {
    this.#stopOfferTanResendTimer();
    const timeTillResendPossible = this.lastTanWasTts ? 20000 : 10000;
    this.#timerHandleOfferTanResend = setTimeout(() => {
      this.offerTanResend = true;
    }, timeTillResendPossible);
  }

  public tanInputValueChange(): void {
    this.#startOfferTanResendTimer();
  }

  #isOngoingPollrequest(authorizationId: string): boolean {
    return !!this.#pollingForAuthSubscriptionsForAuthIds[authorizationId].ongoingPollRequest;
  }
}
